import { getOwner } from '@ember/owner';
import { type Registry as Services, service } from '@ember/service';
import Component from '@glimmer/component';
import { cached } from '@glimmer/tracking';
import { PrismSelectInput } from '@mylighthouse/prism-ember';
import type { IconFlagCode } from '@mylighthouse/prism-ember/types/components/prism/icon-flag';
import Countries from 'frontend-utils/utils/countries';
import {
  type CountryCodeWithDefault,
  DEFAULT_POS,
  type SelectInputOptionWithId,
} from 'lumen/utils/constants/pi-constant-values';

interface ParityDropdownsPosSelectionSignature {
  Args: {
    hasError: boolean;
    isLoading: boolean;
    onChange: (option: SelectInputOptionWithId<CountryCodeWithDefault>) => void;
    posValues: CountryCodeWithDefault[];
    selectedPos: CountryCodeWithDefault;
  };
}

export default class ParityDropdownsPosSelection extends Component<ParityDropdownsPosSelectionSignature> {
  @service declare intl: Services['intl'];

  private countries = new Countries(getOwner(this)!);

  @cached // Preserve reference for isEqual check in PowerSelect code
  get availablePos(): SelectInputOptionWithId<CountryCodeWithDefault>[] {
    if (!this.hasMultiplePosValues) {
      return [this.defaultPos];
    }

    return [
      this.defaultPos,
      ...this.args.posValues.map((pos) => ({
        id: pos,
        label: this.countries.getName(pos),
        iconFlag: pos.toLowerCase() as IconFlagCode,
      })),
    ];
  }

  get defaultPos(): SelectInputOptionWithId<CountryCodeWithDefault> {
    return {
      id: DEFAULT_POS,
      label: this.intl.t('parity.channel_view'),
    };
  }

  get hasMultiplePosValues(): boolean {
    return this.args.posValues.length > 1;
  }

  get isPosDropdownDisabled(): boolean {
    return (
      this.args.isLoading || !this.hasMultiplePosValues || this.args.hasError
    );
  }

  get posDisabledMessage(): string | undefined {
    if (this.args.hasError) {
      return this.intl.t('general.error_short');
    }

    if (this.showPOSUpsellCta) {
      return this.intl.t('parity.multi_pos.cta');
    }

    return undefined;
  }

  get selectedPosOption():
    | SelectInputOptionWithId<CountryCodeWithDefault>
    | undefined {
    // Custom POS can't be displayed until data has been fetched
    if (this.args.isLoading) {
      return this.defaultPos;
    }

    return this.availablePos.find((pos) => pos.id === this.args.selectedPos);
  }

  get showPOSUpsellCta(): boolean {
    return !this.args.isLoading && !this.hasMultiplePosValues;
  }

  <template>
    <PrismSelectInput
      @disabled={{this.isPosDropdownDisabled}}
      @disabledMessage={{this.posDisabledMessage}}
      @onChange={{@onChange}}
      @options={{this.availablePos}}
      @value={{this.selectedPosOption}}
    />
  </template>
}
